import {Injectable} from "@angular/core";
import {DriverLicenseInfo} from "../driver-license-info";
import {LockScreenService} from "../lock-screen.service";
import {Utils} from "../utils";
import {DriverLicenseParsingLogInfo, ParsingStatusValues} from "./driver-license-parsing-log-info";
import {BarcodeFormat, BinaryBitmap, DecodeHintType, HybridBinarizer, RGBLuminanceSource} from '@zxing/library';
import {Observable} from 'rxjs';
import {CirfDriverLicense} from '../../cirf/cirf-driver-license';
import {connectApi} from '../connect-api';
import {CustomHttpParams} from '../connect-http-interceptor';
import {catchError, map, timeout} from 'rxjs/internal/operators';
import {HttpClient} from '@angular/common/http';
import { throwError } from 'rxjs';


@Injectable({
    providedIn: 'root'
})
export class DriverLicenseParsingService {

    private codeReader : any;

    constructor(
        public lockScreenService: LockScreenService,
        private  httpClient: HttpClient) {
    }

    parseAndUploadDriverLicense(formData: FormData, cirfGuid: string, sourceId: string): Observable<CirfDriverLicense> {
        const url = connectApi.parseAndUploadDriverLicense.replace('{cirfGuid}', cirfGuid).replace('{clientId}', sourceId);
        return this.httpClient
            .post<any>(url, formData, {params: new CustomHttpParams(true)})
            .pipe(
                timeout(40000),
                map(result => new CirfDriverLicense(result['CirfDriverLicense'])),
                catchError((err) => {
                    return throwError(err);
                })
            );
    }

    /// deprecated
    parseDriverLicense(file: File, parsingCompleteCallBack: any, failToParseCallBack: any) {

        let t0 = performance.now();
        this.lockScreenService.lockScreen = true;
        // Apply a timeout of 40 seconds to decodeDriverLicense

        import("@zxing/library").then( module => {
             this.codeReader = new module.BrowserPDF417Reader();
            // this.codeReader = new module.MultiFormatReader();
            // const hints = new Map();
            // const formats = [BarcodeFormat.PDF_417];
            // hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
            // hints.set(DecodeHintType.TRY_HARDER, true);
            // this.codeReader.setHints(hints);

            let decodeWithTimeout = Utils.timeoutPromise(40000, this.decodeDriverLicense(file)); //Time out after 40 seconds
            // Wait for the promise to get resolved
            decodeWithTimeout.then((driverLicenseInfo: DriverLicenseInfo) => {
                this.lockScreenService.lockScreen = false;
                let duration = Utils.calculateDuration(t0);
                if(driverLicenseInfo){
                    console.log("decodeDriverLicense took " + duration);
                    if(parsingCompleteCallBack){
                        let driverLicenseParsingLogInfo = this.createDriverLicenseParsingLogInfo(ParsingStatusValues.success, duration,'', file);
                        parsingCompleteCallBack(file, driverLicenseInfo, driverLicenseParsingLogInfo);
                    }
                } else {
                    if(failToParseCallBack){
                        let driverLicenseParsingLogInfo = this.createDriverLicenseParsingLogInfo(ParsingStatusValues.failure, duration,'Failed to parse', file);
                        failToParseCallBack(file, driverLicenseParsingLogInfo);
                    }
                }

            });

            // Wait for the promise to get rejected or timed out
            decodeWithTimeout.catch(error => {
                this.lockScreenService.lockScreen = false;
                // Deal with error
                if(failToParseCallBack){
                    let driverLicenseParsingLogInfo = this.createDriverLicenseParsingLogInfo(ParsingStatusValues.failure, Utils.calculateDuration(t0),'Time out', file);
                    failToParseCallBack(file, driverLicenseParsingLogInfo);
                }
            });

        }).catch(error => {
            this.lockScreenService.lockScreen = false;
            if(failToParseCallBack){
                let driverLicenseParsingLogInfo = this.createDriverLicenseParsingLogInfo(ParsingStatusValues.failure, Utils.calculateDuration(t0),'Error importing parsing library', file);
                failToParseCallBack(file, driverLicenseParsingLogInfo);
            }
        }) ;

    }

    async decodeDriverLicense(file: File): Promise<DriverLicenseInfo>{
        let bmp : ImageBitmap = await createImageBitmap(file);
        return this.decodeDriverLicenseImage(bmp, 0);
    }

    async decodeDriverLicenseImage(bmp: ImageBitmap, yStart: number = 0): Promise<DriverLicenseInfo>{
        let canvas = document.createElement('canvas');
        canvas.width = bmp.width;
        canvas.height = bmp.height;

        let ctx = canvas.getContext('2d');
        ctx.drawImage(bmp, 0, yStart, canvas.width, canvas.height,0, 0, canvas.width*0.7, canvas.height*0.7);

        let img = document.createElement("img");
        img.src = canvas.toDataURL("image/jpg", 1.0);


        // var buffer = ctx.getImageData(0,0, ctx.canvas.width, ctx.canvas.height).data.buffer;
        // var clampedArray = new Uint8ClampedArray(buffer);
        // const luminanceSource = new RGBLuminanceSource(clampedArray, canvas.width, canvas.height);
        //
        // for(let i = 0; i < clampedArray.byteLength; i++){
        //     clampedArray[i] = ( (buffer[i * 4] + buffer[i * 4 + 1] * 2 + buffer[i * 4 + 2]) / 4) & 0xFF;
        // }
        //
        // const binaryBitmap = new BinaryBitmap(new HybridBinarizer(luminanceSource));

        try {
            if(this.codeReader){
                let result = await this.codeReader.decodeFromImage(img);


                //let result = await this.codeReader.decode(binaryBitmap);

                if(result ){
                    return this.parseDriverLicenseText(result.getText());
                }
            }
        } catch (err) {
            console.log(err);
            if(yStart+500 < bmp.height ){
                yStart += 500; //Crop the image from top
                return this.decodeDriverLicenseImage(bmp, yStart);
            } else {
                return null;
            }
        }

    }

    //https://www.dynamsoft.com/blog/imaging/extract-data-pdf417-driver-licenses/
    parseDriverLicenseText(data: string) : DriverLicenseInfo{
        console.log(data);
        if(this.isBCDL(data)){
            return this.parseBCDL(data);
        } else {
            return this.parseOtherPrivincesDL(data)
        }


    }

    isBCDL(data: string) : boolean {
        return data.startsWith('%BC');
    }

    //BC has a different Driver licence format & has to be handled differently
    parseBCDL(data: string) :DriverLicenseInfo {
        let driverLicenseInfo = new DriverLicenseInfo();
        data = this.fixBCDataIssueIfExists(data);
        let dataArray = data.split('^');
        if(dataArray && dataArray.length){
            console.log(dataArray);
            if(dataArray.length >= 1){
                driverLicenseInfo.placeOfIssue = this.extractPlaceOfIssueBC(dataArray[0]);
            }
            if(dataArray.length >= 2){
                this.extractNameBC(dataArray[1], driverLicenseInfo);
            }
            if(dataArray.length >= 3){
                this.extractAddressBC(dataArray[2], driverLicenseInfo);
            }
            if(dataArray.length >= 4){
                this.extractOtherInfoBC(dataArray[3], driverLicenseInfo);
            }

        }
        return driverLicenseInfo;
    }

    fixBCDataIssueIfExists(data: string) : string {
        /*
         * BC driver's licence has a bug with long place of issue city.
         * There is no ^ between the city name and the driver's name
         * The following is a fix for this issue by inserting the ^ on index 15 if it's not exists.
         */
        if(data.indexOf('^') > 15){
            data = data.substring(0,15) + '^' + data.substring(16);
        }
        return data;
    }

    parseOtherPrivincesDL(data: string) :DriverLicenseInfo {
        let dataArray = data.split('\n');
        let driverLicenseInfo = new DriverLicenseInfo();
        driverLicenseInfo.placeOfIssue = this.extractPlaceOfIssue(dataArray);
        driverLicenseInfo.surName = this.extractDataByCode(dataArray, 'DCS');
        //First name code can be DAC or DBP And sometimes it's not preset
        driverLicenseInfo.firstName = this.extractDataByCode(dataArray, 'DAC');
        if(!driverLicenseInfo.firstName){
            driverLicenseInfo.firstName = this.extractDataByCode(dataArray, 'DBP');
        }
        //Middle name code can be DAD or DBQ And sometimes it's not preset
        driverLicenseInfo.middleName = this.extractDataByCode(dataArray, 'DAD');
        if(!driverLicenseInfo.middleName){
            driverLicenseInfo.middleName = this.extractDataByCode(dataArray, 'DBQ');
        }
        driverLicenseInfo.name = this.extractDataByCode(dataArray, 'DCT');
        driverLicenseInfo.genderCode = this.extractDataByCode(dataArray, 'DBC');
        driverLicenseInfo.birthDate = this.extractDataByCode(dataArray, 'DBB');
        driverLicenseInfo.licenseNumber = this.extractDataByCode(dataArray, 'DAQ');
        driverLicenseInfo.streetAddress = this.extractDataByCode(dataArray, 'DAG');
        driverLicenseInfo.city = this.extractDataByCode(dataArray, 'DAI');
        driverLicenseInfo.province = this.extractDataByCode(dataArray, 'DAJ');
        driverLicenseInfo.postalCode = this.extractDataByCode(dataArray, 'DAK');
        driverLicenseInfo.expiryDate = this.extractDataByCode(dataArray, 'DBA');
        return driverLicenseInfo;
    }

    // List of jurisdictions and their codes.
    // source information from https://www.leadtools.com/help/sdk/v20/barcode/api/l-aamva-jurisdiction.html
    jurisdictions = [
        {code:'636028' , label:'British Columbia'},
        {code:'636048' , label:'Manitoba'},
        {code:'636017' , label:'New Brunswick'},
        {code:'636016' , label:'Newfoundland'},
        {code:'636013' , label:'Nova Scotia'},
        {code:'636012' , label:'Ontario'},
        {code:'604426' , label:'Prince Edward Island'},
        {code:'604428' , label:'Quebec'},
        {code:'636044' , label:'Saskatchewan'},
        {code:'604429' , label:'Yukon'},
        {code:'604432' , label:'Alberta'},
        {code:'604433' , label:'Nunavut'},
    ];

    extractPlaceOfIssue(dataArray: string[]) : string{
        let jurisdiction = '';
        if(dataArray && dataArray.length > 1){
            let data = dataArray[1];
            this.jurisdictions.forEach((item)=>{
                if(data.includes(item.code)){
                    jurisdiction = item.label;
                }
            });
            return jurisdiction;
        } else {
            return '';
        }
    }

    extractDataByCode(dataArray: string[], code: string): string {
        let text = dataArray.find(txt=>txt.startsWith(code));
        if(text){
            text = text.slice(code.length).trim();
            text = text.replace(/(^,)|(,$)/g, ""); //remove trailing commas
            return text;
        }
        return '' ;
    }

    extractPlaceOfIssueBC(data: string): string{
        let placeOfIssue = '';
        if(data){
            placeOfIssue = data.substring(3, data.length);
        }
        return placeOfIssue;
    }

    extractNameBC(data: string, driverLicenseInfo: DriverLicenseInfo): void{
        let dataArray = data.split('$');
        if(dataArray.length >= 1){
            driverLicenseInfo.surName = dataArray[0].replace(',', '');
        }
        if(dataArray.length >= 2){
            driverLicenseInfo.firstName = dataArray[1];
        }
    }

    extractAddressBC(data: string, driverLicenseInfo: DriverLicenseInfo) : void {
        let dataArray = data.split('$');
        if(dataArray.length >= 1){
            driverLicenseInfo.streetAddress = dataArray[0];
        }
        if(dataArray.length >= 2){
            driverLicenseInfo.city = dataArray[1].split(' ')[0];
            driverLicenseInfo.province = 'BC';
            driverLicenseInfo.postalCode = dataArray[1].substring(dataArray[1].length-7, dataArray[1].length); //last 7 charachters
        }

    }

    extractOtherInfoBC(data: string, driverLicenseInfo: DriverLicenseInfo) : void {
        let dataArray = data.split('=');
        if(dataArray.length >= 1){
            driverLicenseInfo.licenseNumber = dataArray[0].substring(dataArray[0].length-7, dataArray[0].length); //last 7 digits
        }
        if(dataArray.length >= 2){
            let expirationYear = '20' + dataArray[1].substring(0,2);
            let expirationMonth = dataArray[1].substring(2,4);
            let expirationDay = dataArray[1].substring(dataArray[1].length-2, dataArray[1].length);
            driverLicenseInfo.expiryDate = expirationYear + expirationMonth + expirationDay;
            driverLicenseInfo.birthDate = dataArray[1].substring(4,dataArray[1].length);

        }
    }

    createDriverLicenseParsingLogInfo(status: string, duration: string, reason: string, file: File, completedOnServer: boolean = false): DriverLicenseParsingLogInfo {
        const driverLicenseParsingLogInfo = new DriverLicenseParsingLogInfo();
        driverLicenseParsingLogInfo.status = status;
        driverLicenseParsingLogInfo.failureReason = reason;
        driverLicenseParsingLogInfo.duration = duration;
        driverLicenseParsingLogInfo.fileSize = String(file.size/1000) + ' KB';
        driverLicenseParsingLogInfo.filename = file.name;
        driverLicenseParsingLogInfo.completedOnServer = completedOnServer;

        return driverLicenseParsingLogInfo;
    }
}
